import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Check, Loader, ShoppingCart, RefreshCw, Package, Tag, Users, FileText, MessageSquare, Image, ShoppingBag, Boxes, Webhook, Download, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Definición de recursos disponibles para sincronizar
// Estructura de recursos organizados por etapas secuenciales
const SYNC_STAGES = {
  // ETAPA 1: Categorías (Obligatorio primero)
  stage1: {
    id: 'stage1',
    title: 'Paso 1: Sincronizar Categorías',
    description: 'Primero debes sincronizar las categorías para organizar correctamente tus productos',
    required: true,
    resources: [
      {
        id: 'categories',
        label: 'Categorías',
        description: 'Estructura completa de categorías y subcategorías de tu tienda',
        icon: Tag,
        color: 'green',
        stage: 1
      }
    ]
  },
  
  // ETAPA 2: Productos (Después de categorías)
  stage2: {
    id: 'stage2',
    title: 'Paso 2: Sincronizar Productos Completos',
    description: 'Ahora puedes sincronizar productos con toda su información',
    required: true,
    dependsOn: 'stage1',
    resources: [
      {
        id: 'products',
        label: 'Productos Completos',
        description: 'Marca, Precio, Stock, Imagen, SKU, Código de Barra, Resumen, Descripción, Peso y Combinaciones',
        icon: Package,
        color: 'purple',
        stage: 2
      }
    ]
  },
  
  // ETAPA 3: Resto de recursos (Después de productos)
  stage3: {
    id: 'stage3',
    title: 'Paso 3: Sincronizar Datos Adicionales',
    description: 'Complementa tu integración con órdenes, clientes y más',
    required: false,
    dependsOn: 'stage2',
    resources: [
      {
        id: 'orders',
        label: 'Órdenes / Pedidos',
        description: 'Historial de pedidos completados con detalles',
        icon: ShoppingBag,
        color: 'orange',
        stage: 3
      },
      {
        id: 'customers',
        label: 'Clientes',
        description: 'Información de clientes: nombres, emails, teléfonos',
        icon: Users,
        color: 'teal',
        stage: 3
      },
      {
        id: 'messages',
        label: 'Mensajes e Hilos',
        description: 'Conversaciones y mensajes de clientes',
        icon: MessageSquare,
        color: 'pink',
        stage: 3
      },
      {
        id: 'abandoned_carts',
        label: 'Carritos Abandonados',
        description: 'Carritos no finalizados para estrategias de recuperación',
        icon: ShoppingCart,
        color: 'red',
        stage: 3
      }
    ]
  }
};

// Metadatos de cada recurso para la vista de resincronización
const RESOURCE_META = {
  categories: { label: 'Categorías', icon: Tag },
  products: { label: 'Productos', icon: Package },
  stock: { label: 'Stock de Productos', icon: Boxes },
  orders: { label: 'Órdenes / Pedidos', icon: ShoppingBag },
  customers: { label: 'Clientes', icon: Users },
  messages: { label: 'Mensajes e Hilos', icon: MessageSquare },
  abandoned_carts: { label: 'Carritos Abandonados', icon: ShoppingCart },
};

const fmtDate = (iso) => {
  if (!iso) return 'Nunca';
  try {
    return new Date(iso).toLocaleString('es-CL', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Línea comparadora Local vs PrestaShop para un recurso
const CompareLine = ({ res }) => {
  if (!res) return null;
  const remoteTxt = res.remote === null || res.remote === undefined ? '…' : res.remote;
  const diff = typeof res.diff === 'number' ? res.diff : null;
  const isTarget = res.kind === 'target'; // stock: X de Z productos, no "novedades"
  const atDay = isTarget
    ? (typeof res.remote === 'number' && res.local >= res.remote && res.local > 0)
    : diff === 0;
  return (
    <div className="text-xs text-slate-700 space-y-0.5">
      <p>
        {isTarget ? (
          <>
            Sincronizado <span className="font-bold">{res.local}</span> productos con
            existencias de <span className="font-bold">{remoteTxt}</span> Productos
          </>
        ) : (
          <>
            <span className="font-bold">NF:</span> {res.local}
            <span className="mx-1.5 text-slate-400">·</span>
            <span className="font-bold">PrestaShop:</span> {remoteTxt}
          </>
        )}
        {!isTarget && diff !== null && diff > 0 && (
          <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-500 text-amber-800 font-bold">
            +{diff} en PrestaShop
          </span>
        )}
        {atDay && (
          <span className="ml-2 px-1.5 py-0.5 rounded bg-green-100 border border-green-500 text-green-800 font-bold">
            al día
          </span>
        )}
      </p>
      <p className="text-slate-500">Última sincronización: {fmtDate(res.last_sync)}</p>
    </div>
  );
};

// Barra Y/Z para un recurso en sincronización
const ResourceBar = ({ done, total }) => {
  if (!total) return (
    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
      <Loader className="w-3 h-3 animate-spin" /> Sincronizando…
    </p>
  );
  const pct = Math.min(100, Math.round((done / total) * 100));
  return (
    <div className="mt-1.5">
      <div className="flex justify-between text-xs font-bold text-slate-700 mb-0.5">
        <span>Sincronizando…</span>
        <span>{done} / {total}</span>
      </div>
      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden border border-slate-400">
        <div className="h-full bg-slate-900 transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const ResyncView = ({
  status, loading, syncing, stockSyncing, resourceProgress, batchProgress, stockProgress,
  webhookReady, onConfigureWebhook,
  onReloadStatus, onSyncCategories, onSyncProducts, onSyncStock, onSyncExtras,
}) => {
  const resources = status?.resources || {};
  const [selectedExtras, setSelectedExtras] = useState({});

  // Progreso Y/Z por recurso: del job (resourceProgress), del batch de productos o del stock
  const progressFor = (resKey) => {
    if (resKey === 'products' && batchProgress) {
      return { done: batchProgress.synced_products || 0, total: batchProgress.total_products || 0 };
    }
    if (resKey === 'stock' && (stockSyncing || stockProgress)) {
      return { done: stockProgress?.done || 0, total: stockProgress?.total || 0 };
    }
    if (resourceProgress && resourceProgress.resource === resKey) {
      return { done: resourceProgress.done || 0, total: resourceProgress.total || 0 };
    }
    return null;
  };

  const extraKeys = ['orders', 'customers', 'messages', 'abandoned_carts'];
  const withDiff = Object.entries(resources).filter(
    ([, r]) => typeof r?.diff === 'number' && r.diff > 0
  ).length;

  const toggleExtra = (k) =>
    setSelectedExtras((p) => ({ ...p, [k]: !p[k] }));

  const runExtras = () => {
    const list = extraKeys.filter((k) => selectedExtras[k]);
    if (list.length === 0) {
      toast.error('Selecciona al menos un recurso');
      return;
    }
    onSyncExtras(list);
  };

  const BaseCard = ({ resKey, onSync }) => {
    const meta = RESOURCE_META[resKey];
    const res = resources[resKey];
    const Icon = meta.icon;
    const prog = progressFor(resKey);
    return (
      <div className="border-2 border-slate-900 rounded-xl p-4 bg-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <Icon className="w-6 h-6 flex-shrink-0 text-slate-700" />
            <div className="flex-1">
              <h4 className="font-bold text-slate-900 flex items-center gap-2">
                {meta.label}
                {res?.synced && <Check className="w-4 h-4 text-green-600" />}
              </h4>
              <div className="mt-1">
                {prog ? (
                  <ResourceBar done={prog.done} total={prog.total} />
                ) : loading ? (
                  <p className="text-xs text-slate-400">Comparando…</p>
                ) : (
                  <CompareLine res={res} />
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onSync}
            disabled={syncing || stockSyncing}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-700 disabled:opacity-50 whitespace-nowrap"
          >
            <RefreshCw className="w-4 h-4" />
            Resincronizar
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Banner resumen */}
      <div className={`border-2 rounded-xl p-4 ${
        loading ? 'bg-slate-50 border-slate-300'
          : withDiff > 0 ? 'bg-amber-50 border-amber-600'
          : 'bg-green-50 border-green-600'
      }`}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-bold text-slate-900">
              {loading
                ? 'Comparando con PrestaShop…'
                : withDiff > 0
                ? `⚠️ ${withDiff} recurso(s) con novedades en PrestaShop`
                : '✅ Todo al día'}
            </h3>
            <p className="text-sm text-slate-700">
              La integración funciona automáticamente por webhook. Resincroniza solo si
              necesitas traer cambios masivos o datos históricos.
            </p>
          </div>
          <button
            onClick={onReloadStatus}
            disabled={loading}
            className="p-2 bg-white border-2 border-slate-900 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            title="Volver a comparar"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Recursos base: Categorías y Productos (flujo estricto: siempre primero) */}
      <div>
        <p className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-2">
          Recursos base
        </p>
        <div className="space-y-3">
          <BaseCard resKey="categories" onSync={onSyncCategories} />
          <BaseCard resKey="products" onSync={onSyncProducts} />
          <BaseCard resKey="stock" onSync={onSyncStock} />
        </div>
        <p className="text-xs text-slate-500 mt-1.5">
          El stock se trae con los productos y se refina automáticamente al terminar
          (fuente autoritativa: PrestaShop).
        </p>
      </div>

      {/* Recursos adicionales */}
      <div>
        <p className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-2">
          Recursos adicionales
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {extraKeys.map((k) => {
            const meta = RESOURCE_META[k];
            const res = resources[k];
            const Icon = meta.icon;
            const checked = !!selectedExtras[k];
            const prog = progressFor(k);
            return (
              <div
                key={k}
                onClick={() => !prog && toggleExtra(k)}
                className={`border-2 rounded-xl p-3 transition-all ${prog ? '' : 'cursor-pointer'} ${
                  prog ? 'bg-white border-slate-900'
                    : checked ? 'bg-blue-50 border-blue-600'
                    : 'bg-slate-50 border-slate-300 hover:border-slate-400'
                }`}
              >
                <div className="flex items-start gap-2">
                  <input type="checkbox" checked={checked} onChange={() => {}} disabled={!!prog} className="w-5 h-5 mt-0.5 cursor-pointer" />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <Icon className="w-4 h-4" />
                      <span className="font-bold text-slate-900 text-sm">{meta.label}</span>
                      {res?.synced && <Check className="w-4 h-4 text-green-600" />}
                    </div>
                    <div className="mt-1">
                      {prog ? (
                        <ResourceBar done={prog.done} total={prog.total} />
                      ) : loading ? (
                        <p className="text-xs text-slate-400">Comparando…</p>
                      ) : (
                        <CompareLine res={res} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-3">
          <button
            onClick={runExtras}
            disabled={syncing}
            className="flex items-center gap-2 px-5 py-3 bg-blue-500 text-white border-2 border-slate-900 rounded-xl font-bold hover:bg-blue-600 disabled:opacity-50"
            style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
          >
            {syncing ? <Loader className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
            Sincronizar seleccionados
          </button>
          <button
            onClick={onConfigureWebhook}
            disabled={!webhookReady}
            title={webhookReady ? '' : 'Disponible cuando categorías y productos estén sincronizados'}
            className="flex items-center gap-2 px-5 py-3 bg-pink-500 text-white border-2 border-slate-900 rounded-xl font-bold hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
          >
            <Webhook className="w-5 h-5" />
            Configurar Webhook
          </button>
        </div>
      </div>

      {/* Ventajas del webhook (aparece cuando ya se puede configurar) */}
      {webhookReady && <WebhookBenefits onConfigure={onConfigureWebhook} />}
    </div>
  );
};

const WebhookBenefits = ({ onConfigure }) => (
  <div className="border-2 border-pink-600 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-4 space-y-3">
    <div className="flex items-center gap-2">
      <Webhook className="w-5 h-5 text-pink-600" />
      <h3 className="font-bold text-slate-900">Activa la sincronización en tiempo real</h3>
    </div>
    <p className="text-sm text-slate-700">
      Con el webhook configurado no necesitas volver a pulsar "Resincronizar": cada cambio
      en PrestaShop se refleja al instante en Negocio Feliz, y cada venta en tu POS baja el
      stock en PrestaShop.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
      <div className="bg-white border-2 border-slate-900 rounded-lg p-3">
        <p className="font-bold text-slate-900 mb-1">PrestaShop → Negocio Feliz</p>
        <ul className="space-y-1 text-slate-700 list-disc ml-4">
          <li>Producto creado o editado: precio, SKU, <strong>imagen, descripción, resumen, marca, categoría</strong>, peso, código de barra</li>
          <li>Producto eliminado</li>
          <li>Cambio de stock</li>
          <li>Pedido nuevo y cambios de estado</li>
          <li>Cliente creado o editado</li>
        </ul>
      </div>
      <div className="bg-white border-2 border-slate-900 rounded-lg p-3">
        <p className="font-bold text-slate-900 mb-1">Negocio Feliz → PrestaShop</p>
        <ul className="space-y-1 text-slate-700 list-disc ml-4">
          <li>Venta en el POS: descuenta el stock del producto en PrestaShop</li>
        </ul>
      </div>
    </div>

    <div className="bg-amber-50 border-2 border-amber-500 rounded-lg p-3 text-xs text-amber-900">
      <strong>Importante:</strong> las <strong>imágenes</strong> y <strong>descripciones</strong> de
      los productos <strong>no llegan con la sincronización masiva</strong> (el API de PrestaShop
      falla al traerlas en lote). Solo llegan por <strong>webhook</strong>, producto a producto,
      cuando se crea o edita en PrestaShop. Con el webhook activo, cada producto nuevo o modificado
      traerá su imagen y descripción automáticamente.
    </div>

    <button
      onClick={onConfigure}
      className="flex items-center gap-2 px-4 py-2.5 bg-pink-500 text-white border-2 border-slate-900 rounded-lg font-bold hover:bg-pink-600"
      style={{ boxShadow: '3px 3px 0px 0px rgba(15,23,42,1)' }}
    >
      <Webhook className="w-4 h-4" />
      Configurar Webhook ahora
    </button>
  </div>
);

// Modal para configurar el webhook: URL a pegar en PrestaShop + descarga del módulo
const WebhookConfigModal = ({ integrationId, onClose }) => {
  const webhookUrl = `${BACKEND_URL}/api/integrations/webhooks/prestashop/${integrationId}`;
  const isLocal = /localhost|127\.0\.0\.1|0\.0\.0\.0/.test(BACKEND_URL || '');

  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl).then(
      () => toast.success('URL de Webhook copiada'),
      () => {
        const ta = document.createElement('textarea');
        ta.value = webhookUrl;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); toast.success('URL de Webhook copiada'); }
        catch { toast.error('No se pudo copiar'); }
        document.body.removeChild(ta);
      }
    );
  };

  const downloadModule = () => {
    const link = document.createElement('a');
    link.href = `${API}/integrations/prestashop/download-module`;
    link.download = 'negociofeliz_webhooks.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Descargando módulo de PrestaShop…');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
        <div
          className="bg-white border-4 border-slate-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          style={{ boxShadow: '12px 12px 0px 0px rgba(15,23,42,1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-pink-500 to-rose-600 text-white px-6 py-4 flex justify-between items-center border-b-4 border-slate-900">
            <div className="flex items-center gap-3">
              <Webhook className="w-6 h-6" />
              <h2 className="text-xl font-bold">Configurar Webhook</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            <p className="text-sm text-slate-700">
              El webhook mantiene la sincronización automática en tiempo real: cada cambio
              en PrestaShop (productos, stock, pedidos, clientes) se refleja al instante en
              Negocio Feliz, y cada venta del POS baja el stock en PrestaShop.
            </p>
            <p className="text-xs text-slate-600 bg-slate-50 border-2 border-slate-300 rounded-lg p-2.5">
              💡 Las <strong>imágenes</strong> y <strong>descripciones</strong> de los productos
              solo llegan por webhook (el API no las trae en la sincronización masiva). Con el
              webhook activo, cada producto que se cree o edite en PrestaShop las traerá.
            </p>

            {isLocal && (
              <div className="bg-amber-50 border-2 border-amber-500 rounded-lg p-3 text-xs text-amber-900">
                <strong>⚠️ Entorno local:</strong> esta URL apunta a tu equipo
                (<span className="font-mono">{BACKEND_URL}</span>) y PrestaShop no puede
                alcanzarla desde internet. Para probar el webhook en local necesitas un
                túnel (cloudflared / ngrok) que exponga el backend con una URL pública, y
                usar esa URL aquí. En producción, con el sistema publicado en un dominio,
                funciona directo.
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">1 · URL del Webhook</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={webhookUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 px-3 py-2 border-2 border-slate-900 rounded-lg font-mono text-xs bg-slate-50"
                />
                <button
                  onClick={copyUrl}
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-700 whitespace-nowrap"
                >
                  <Copy className="w-4 h-4" /> Copiar
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Pégala en la configuración del módulo, campo <strong>URL de Webhook</strong>.
              </p>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">2 · Módulo de PrestaShop</label>
              <button
                onClick={downloadModule}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white border-2 border-slate-900 rounded-xl font-bold hover:bg-green-600 transition-all"
                style={{ boxShadow: '3px 3px 0px 0px rgba(15,23,42,1)' }}
              >
                <Download className="w-5 h-5" />
                Descargar Módulo PrestaShop (.zip)
              </button>
              <p className="text-xs text-slate-500 mt-1">
                Instálalo en <strong>Módulos → Subir un módulo</strong>, ábrelo con
                <strong> Configurar</strong>, pega la URL de arriba y activa los webhooks.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-slate-100 border-2 border-slate-900 rounded-xl font-bold hover:bg-slate-200 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PrestashopModal = ({ isOpen, onClose, integration, onSuccess, stores }) => {
  const [step, setStep] = useState(1); // 1: Conexión, 2: Sincronización
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Formulario de conexión
  const [shopUrl, setShopUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Sincronización
  const [integrationId, setIntegrationId] = useState(null);
  const [syncingCategories, setSyncingCategories] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [categoriesCount, setCategoriesCount] = useState(0);
  const [productsCount, setProductsCount] = useState(0);
  const [syncProgress, setSyncProgress] = useState(0); // Porcentaje 0-100
  
  // Recursos a sincronizar (checkboxes)
  const [syncResources, setSyncResources] = useState({
    products: true,          // Productos (incluye marcas) - ESENCIAL
    categories: true,        // Categorías - ESENCIAL
    customers: false,        // Clientes
    orders: true,            // Órdenes/Pedidos - ESENCIAL
    messages: false,         // Mensajes e hilos de clientes
    images: true,            // Imágenes de productos - ESENCIAL
    abandoned_carts: false,  // Carritos abandonados
    completed_carts: false,  // Carritos finalizados
    stock: true,             // Stock de productos - ESENCIAL
    prices: true             // Precios de productos - ESENCIAL
  });
  
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState({});
  // Progreso Y/Z del recurso en curso: { resource, done, total }
  const [resourceProgress, setResourceProgress] = useState(null);
  // Progreso del sync de stock: { done, total } o null
  const [stockProgress, setStockProgress] = useState(null);
  const [showWebhookConfig, setShowWebhookConfig] = useState(false);
  // true mientras corre la configuración inicial (categorías -> productos encadenados)
  const firstRunRef = useRef(false);
  const resyncShownRef = useRef(false); // ya se hizo el salto a la vista resync
  
  // Estados para sincronización por lotes profesional
  const [batchSyncing, setBatchSyncing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({
    status: 'idle',
    progress_percentage: 0,
    current_batch: 0,
    total_batches: 0,
    synced_products: 0,
    failed_products: 0,
    total_products: 0,
    job_id: null
  });
  const [showBatchReport, setShowBatchReport] = useState(false);
  
  // Estados para sincronización de stock (Fase 2)
  const [syncingStock, setSyncingStock] = useState(false);
  const [stockConflicts, setStockConflicts] = useState([]);
  const [showConflictsModal, setShowConflictsModal] = useState(false);
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  
  // Estados para etapas de sincronización secuencial
  const [completedStages, setCompletedStages] = useState({
    stage1: false,    // Etapa 1: Categorías
    stage2: false,    // Etapa 2: Productos
    stage3: false     // Etapa 3: Resto (órdenes, clientes, etc.)
  });

  const [currentStage, setCurrentStage] = useState('stage1'); // Etapa actual visible

  // Estado + comparador con PrestaShop (endpoint sync-status)
  const [syncStatus, setSyncStatus] = useState(null);      // { resources: { categories: {local, remote, diff, synced, last_sync}, ... } }
  const [loadingStatus, setLoadingStatus] = useState(false);

  const loadSyncStatus = useCallback(async (integId) => {
    if (!integId) return;
    setLoadingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const { data } = await axios.get(
        `${API}/integrations/prestashop/${integId}/sync-status`,
        { headers }
      );
      setSyncStatus(data);

      const r = data.resources || {};
      const hasCategories = !!r.categories?.synced;
      const hasProducts = !!r.products?.synced;
      const hasExtras = ['orders', 'customers', 'messages', 'abandoned_carts']
        .some((k) => r[k]?.synced);

      setCompletedStages({ stage1: hasCategories, stage2: hasProducts, stage3: hasExtras });

      // Durante la configuración inicial ya decidimos la vista manualmente
      // (salto a 'resync' al terminar categorías); no dejar que este refresco la pise.
      if (!firstRunRef.current) {
        if (hasCategories || hasProducts) {
          setCurrentStage('resync'); // integración existente -> vista unificada
        } else {
          setCurrentStage('stage1'); // primera vez -> flujo estricto
        }
      }
    } catch (error) {
      console.error('Error obteniendo estado de sincronización:', error);
      // No dejar el modal atascado en "cargando": mostrar la vista con datos vacíos
      setSyncStatus((prev) => prev || { resources: {} });
      if (!firstRunRef.current) setCurrentStage('resync');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (integration) {
      setShopUrl(integration.shop_url);
      setSelectedStoreId(integration.store_id);
      setIntegrationId(integration.id);
      setConnectionStatus('connected');
      setStep(2); // Si ya existe integración, ir directo a sincronización
      loadSyncStatus(integration.id);
    }
  }, [integration, loadSyncStatus]);

  const handleTestConnection = async () => {
    if (!shopUrl || !apiKey) {
      toast.error('Completa todos los campos');
      return;
    }

    setTesting(true);
    try {
      // Intentar conectar (esto también valida la conexión)
      await axios.post(`${API}/integrations/prestashop/connect`, {
        shop_url: shopUrl,
        api_key: apiKey,
        store_id: selectedStoreId
      });
      
      setConnectionStatus('success');
      toast.success('✓ Conexión exitosa con PrestaShop');
    } catch (error) {
      setConnectionStatus('error');
      
      // Manejar diferentes tipos de errores
      let errorMsg = 'Error al conectar con PrestaShop';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Si es un error de validación de Pydantic (422)
        if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(err => err.msg).join(', ');
        } else if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (data.message) {
          errorMsg = data.message;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    
    if (!shopUrl || !apiKey || !selectedStoreId) {
      toast.error('Completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/integrations/prestashop/connect`, {
        shop_url: shopUrl,
        api_key: apiKey,
        store_id: selectedStoreId
      });
      
      setIntegrationId(response.data.integration_id);
      setConnectionStatus('connected');
      toast.success('Integración configurada exitosamente');
      
      // Refrescar lista de integraciones inmediatamente
      onSuccess();
      
      setStep(2); // Pasar a sincronización
    } catch (error) {
      // Manejar diferentes tipos de errores
      let errorMsg = 'Error al configurar integración';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Si es un error de validación de Pydantic (422)
        if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(err => err.msg).join(', ');
        } else if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (data.message) {
          errorMsg = data.message;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncResources = async (resources = null) => {
    if (!integrationId) return;
    
    // Si no se pasan recursos, obtener de syncResources
    const selectedResources = resources || Object.keys(syncResources).filter(key => syncResources[key]);
    
    if (selectedResources.length === 0) {
      toast.error('Selecciona al menos un recurso para sincronizar');
      return;
    }

    setSyncing(true);
    setSyncProgress(0);
    setSyncResults({});
    
    try {
      // Iniciar sincronización
      const response = await axios.post(
        `${API}/integrations/prestashop/${integrationId}/sync`,
        { resources: selectedResources }
      );
      
      const jobId = response.data.job_id;
      
      // Polling para verificar progreso
      const pollInterval = setInterval(async () => {
        try {
          const jobResponse = await axios.get(`${API}/integrations/jobs/${jobId}`);
          const job = jobResponse.data;
          
          // Actualizar progreso
          setSyncProgress(job.progress || 0);
          setSyncResults(job.results || {});
          setResourceProgress(job.resource_progress || null);

          if (job.status === 'completed') {
            clearInterval(pollInterval);
            setSyncProgress(100);
            setSyncing(false);
            setResourceProgress(null);
            
            // Mostrar resumen
            const totalSynced = Object.values(job.results || {}).reduce((sum, val) => sum + (val || 0), 0);
            toast.success(`✓ Sincronización completada: ${totalSynced} elementos`, {
              duration: 5000
            });
            
            if (selectedResources.includes('categories')) {
              setCompletedStages(prev => ({ ...prev, stage1: true }));
              if (firstRunRef.current) {
                // Categorías listas: saltar YA a la vista unificada de recursos y
                // encadenar la sincronización de productos (se verá su avance en la
                // tarjeta de Productos).
                resyncShownRef.current = true;
                setCurrentStage('resync');
                loadSyncStatus(integrationId);
                handleBatchSync();
              }
            }

            // Refrescar el comparador (salvo en la config inicial, que sigue con productos)
            if (!firstRunRef.current) {
              loadSyncStatus(integrationId);
            }

            // DISPARAR EVENTO GLOBAL para recargar categorías
            window.dispatchEvent(new CustomEvent('reloadCategories'));
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            setSyncing(false);
            setResourceProgress(null);
            toast.error(`Error: ${job.message}`);
          }
        } catch (error) {
          console.error('Error polling job:', error);
        }
      }, 2000);
      
      // Timeout de seguridad (10 minutos)
      setTimeout(() => {
        clearInterval(pollInterval);
        if (syncing) {
          setSyncing(false);
          toast.error('Sincronización tomó demasiado tiempo. Verifica el estado más tarde.');
        }
      }, 600000);
      
    } catch (error) {
      let errorMsg = 'Error al sincronizar recursos';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(err => err.msg).join(', ');
        } else if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (data.message) {
          errorMsg = data.message;
        }
      }
      
      toast.error(errorMsg);
      setSyncing(false);
    }
  };

  // Configuración inicial: categorías -> (encadenado) productos -> vista resync
  const handleFirstRun = () => {
    firstRunRef.current = true;
    resyncShownRef.current = false;
    handleSyncResources(['categories']);
  };

  // Sincronización por lotes profesional (para productos)
  const handleBatchSync = async () => {
    if (!integrationId) return;
    
    setBatchSyncing(true);
    setBatchProgress({
      status: 'starting',
      progress_percentage: 0,
      current_batch: 0,
      total_batches: 0,
      synced_products: 0,
      failed_products: 0,
      total_products: 0,
      job_id: null
    });
    
    try {
      // Iniciar sincronización RÁPIDA (optimizada con offset)
      const response = await axios.post(
        `${API}/integrations/prestashop/${integrationId}/sync-products-fast`
      );
      
      const jobId = response.data.job_id;
      setBatchProgress(prev => ({ ...prev, job_id: jobId, status: 'running' }));
      
      toast.success('🚀 Sincronización rápida iniciada', {
        duration: 5000
      });
      
      // Polling para verificar progreso
      const pollInterval = setInterval(async () => {
        try {
          const progressResponse = await axios.get(
            `${API}/integrations/prestashop/sync-progress-v2/${jobId}`
          );
          
          const progress = progressResponse.data;
          
          // El BatchSyncService puede devolver status 'running', 'completed', 'error'
          if (!progress) {
            return;
          }
          
          setBatchProgress({
            status: progress.status,
            progress_percentage: progress.progress_percentage || 0,
            current_batch: progress.current_batch || 0,
            total_batches: progress.total_batches || 0,
            synced_products: progress.synced_products || 0,
            failed_products: progress.failed_products || 0,
            total_products: progress.total_products || 0,
            products_created: progress.products_created || 0,
            products_updated: progress.products_updated || 0,
            products_skipped: progress.products_skipped || 0,
            job_id: jobId,
            errors_count: progress.errors_count || 0,
            incomplete_count: progress.incomplete_count || 0,
            report_path: progress.report_path || null
          });

          // Fallback: asegurar la vista unificada si por algo no se hizo el salto
          if (firstRunRef.current && !resyncShownRef.current) {
            resyncShownRef.current = true;
            setCurrentStage('resync');
          }

          if (progress.status === 'completed') {
            clearInterval(pollInterval);
            setBatchSyncing(false);
            if (firstRunRef.current) {
              firstRunRef.current = false;
              setCurrentStage('resync');
            }
            // El stock viene con los productos; refrescar el stock autoritativo desde PS
            handleSyncStockFromPrestaShop();
            
            // Mensaje detallado con estadísticas
            const stats = [
              `✓ ${progress.synced_products} productos`,
              progress.products_created > 0 ? `✨ ${progress.products_created} nuevos` : null,
              progress.products_updated > 0 ? `🔄 ${progress.products_updated} actualizados` : null,
              progress.products_skipped > 0 ? `⏩ ${progress.products_skipped} sin cambios` : null,
              progress.failed_products > 0 ? `❌ ${progress.failed_products} fallidos` : null
            ].filter(Boolean).join(' | ');
            
            // Comparar con total disponible
            const completeness = progress.total_products && progress.total_products > progress.synced_products
              ? `\n⚠️  ${progress.synced_products}/${progress.total_products} sincronizados`
              : `\n✅ 100% completado`;
            
            toast.success(`Sincronización completada\n${stats}${completeness}`, { duration: 8000 });
            
            // Productos sincronizados -> a la vista unificada de resync
            setCompletedStages(prev => ({ ...prev, stage2: true }));
            setCurrentStage('resync');

            // Refrescar el comparador
            loadSyncStatus(integrationId);

            // Mostrar opción de descargar reporte si hay errores
            if (progress.failed_products > 0) {
              setShowBatchReport(true);
            }

            // Recargar categorías si corresponde
            window.dispatchEvent(new CustomEvent('reloadCategories'));
          } else if (progress.status === 'failed') {
            clearInterval(pollInterval);
            setBatchSyncing(false);
            toast.error(`Error en sincronización: ${progress.error || 'Error desconocido'}`);
          }
        } catch (error) {
          console.error('Error polling batch progress:', error);
        }
      }, 2000); // Polling cada 2 segundos
      
      // Timeout de seguridad aumentado (15 minutos, más que suficiente para 10k productos)
      setTimeout(() => {
        clearInterval(pollInterval);
        if (batchSyncing) {
          setBatchSyncing(false);
          toast.warning('⏱️ Sincronización tomó más de lo esperado. Verifica en Inventario.');
        }
      }, 900000); // 15 minutos (vs 30 minutos anterior, ahora es mucho más rápido)
      
    } catch (error) {
      let errorMsg = 'Error al iniciar sincronización por lotes';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (data.message) {
          errorMsg = data.message;
        }
      }
      
      toast.error(errorMsg);
      setBatchSyncing(false);
    }
  };
  
  const downloadBatchReport = async () => {
    if (!batchProgress.job_id) return;
    
    try {
      const response = await axios.get(
        `${API}/sync-reports/${batchProgress.job_id}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sync_report_${batchProgress.job_id}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Reporte descargado');
    } catch (error) {
      toast.error('Error descargando reporte');
    }
  };

  // ============================================================================
  // FUNCIONES DE SINCRONIZACIÓN DE STOCK (FASE 2)
  // ============================================================================
  
  const handleSyncStockFromPrestaShop = async () => {
    const iid = integrationId || integration?.id;
    if (!iid) return;

    setSyncingStock(true);
    setStockProgress({ done: 0, total: 0 });
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const { data } = await axios.post(
        `${API}/integrations/prestashop/${iid}/stock/sync-from-ps`,
        { product_ids: null },
        { headers }
      );
      const jobId = data.job_id;
      if (!jobId) { setSyncingStock(false); setStockProgress(null); return; }

      const poll = setInterval(async () => {
        try {
          const { data: job } = await axios.get(`${API}/integrations/jobs/${jobId}`, { headers });
          if (job.resource_progress?.resource === 'stock') {
            setStockProgress({ done: job.resource_progress.done, total: job.resource_progress.total });
          }
          if (job.status === 'completed' || job.status === 'failed') {
            clearInterval(poll);
            setSyncingStock(false);
            setStockProgress(null);
            const r = job.results || {};
            if (job.status === 'completed') {
              const changed = r.updated_count ?? r.synced_count ?? 0;
              toast.success(`✅ Stock actualizado desde PrestaShop (${changed})`, { duration: 4000 });
            }
            loadSyncStatus(iid);
            if (onSuccess) onSuccess();
          }
        } catch (e) { /* reintentará */ }
      }, 2000);

      setTimeout(() => { clearInterval(poll); setSyncingStock(false); setStockProgress(null); }, 900000);
    } catch (error) {
      console.error('Error sincronizando stock:', error);
      setSyncingStock(false);
      setStockProgress(null);
    }
  };
  
  const handleDetectConflicts = async () => {
    if (!integration?.id) return;
    
    setLoadingConflicts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/integrations/prestashop/${integration.id}/stock/conflicts`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setStockConflicts(response.data.conflicts || []);
      setShowConflictsModal(true);
      
      if (response.data.conflicts && response.data.conflicts.length > 0) {
        toast.warning(`⚠️ Se encontraron ${response.data.total_conflicts} conflictos de stock`);
      } else {
        toast.success('✅ No hay conflictos de stock');
      }
    } catch (error) {
      console.error('Error detectando conflictos:', error);
      toast.error('Error al detectar conflictos de stock');
    } finally {
      setLoadingConflicts(false);
    }
  };
  
  const handleResolveConflict = async (productId, resolution, customQuantity = null) => {
    if (!integration?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/integrations/prestashop/stock/resolve-conflict`,
        {
          integration_id: integration.id,
          product_id: productId,
          resolution: resolution, // 'use_local', 'use_prestashop', 'use_custom'
          custom_quantity: customQuantity
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('✅ Conflicto resuelto');
      
      // Recargar conflictos
      handleDetectConflicts();
      
      // Recargar productos
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error resolviendo conflicto:', error);
      toast.error(error.response?.data?.detail || 'Error al resolver conflicto');
    }
  };


  const handleFinish = () => {
    onSuccess();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div 
          className="bg-white border-4 border-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{ boxShadow: '12px 12px 0px 0px rgba(15,23,42,1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-500 to-rose-600 text-white px-6 py-4 flex justify-between items-center border-b-4 border-slate-900">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-6 h-6" />
              <h2 className="text-xl font-bold">
                {integration ? 'Configurar' : 'Conectar'} PrestaShop
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Steps indicator */}
          <div className="px-6 py-4 bg-slate-50 border-b-2 border-slate-200">
            <div className="flex items-center justify-center gap-4">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-pink-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold ${
                  step >= 1 ? 'bg-pink-100 border-pink-600' : 'border-slate-300'
                }`}>
                  {connectionStatus === 'connected' ? <Check className="w-5 h-5" /> : '1'}
                </div>
                <span className="font-semibold text-sm">Conexión</span>
              </div>
              
              <div className={`w-16 h-0.5 ${step >= 2 ? 'bg-pink-600' : 'bg-slate-300'}`} />
              
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-pink-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold ${
                  step >= 2 ? 'bg-pink-100 border-pink-600' : 'border-slate-300'
                }`}>
                  2
                </div>
                <span className="font-semibold text-sm">Sincronización</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 1 && (
              <form onSubmit={handleConnect} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    URL de tu tienda PrestaShop
                  </label>
                  <input
                    type="url"
                    value={shopUrl}
                    onChange={(e) => setShopUrl(e.target.value)}
                    placeholder="https://tu-tienda.com"
                    className="w-full px-4 py-3 border-2 border-slate-900 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-pink-500"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Ejemplo: https://tricomarpets.cl
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    API Key de PrestaShop
                  </label>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="WN6A2FY5787TFLFXQ16VTM21377PDU5V"
                    className="w-full px-4 py-3 border-2 border-slate-900 rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Encuentra tu API Key en: PrestaShop → Parámetros Avanzados → Webservice
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Tienda/Caja asociada
                  </label>
                  <select
                    value={selectedStoreId}
                    onChange={(e) => setSelectedStoreId(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-900 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-pink-500"
                    required
                  >
                    <option value="">Selecciona una tienda...</option>
                    {stores && stores.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name} ({store.code})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Esta será el canal de ventas de PrestaShop
                  </p>
                  {stores && stores.length === 0 && (
                    <div className="mt-2 bg-yellow-50 border-2 border-yellow-600 rounded-lg p-3">
                      <p className="text-xs text-yellow-900 font-semibold">
                        ⚠️ No tienes tiendas configuradas. Ve a Configuración → Tiendas para crear una tienda primero.
                      </p>
                    </div>
                  )}
                </div>

                {connectionStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-600 rounded-lg p-4">
                    <p className="text-sm text-red-900 font-semibold">
                      ✗ No se pudo conectar. Verifica la URL y API Key.
                    </p>
                  </div>
                )}

                {connectionStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-600 rounded-lg p-4">
                    <p className="text-sm text-green-900 font-semibold">
                      ✓ Conexión exitosa con PrestaShop
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testing || !shopUrl || !apiKey || !selectedStoreId}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-100 text-blue-900 border-2 border-slate-900 rounded-xl px-6 py-3 font-bold hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {testing ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Probando...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        Probar Conexión
                      </>
                    )}
                  </button>

                  <button
                    type="submit"
                    disabled={loading || connectionStatus !== 'success'}
                    className="flex-1 flex items-center justify-center gap-2 bg-pink-500 text-white border-2 border-slate-900 rounded-xl px-6 py-3 font-bold hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Conectando...
                      </>
                    ) : (
                      <>
                        Conectar y Continuar
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {step === 2 && integration && !syncStatus && (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader className="w-10 h-10 animate-spin text-pink-500" />
                <p className="text-sm text-slate-600 font-semibold">
                  {loadingStatus ? 'Comparando con PrestaShop…' : 'Cargando estado de la integración…'}
                </p>
              </div>
            )}

            {step === 2 && (!integration || syncStatus) && (
              <div className="space-y-6">
                {/* Badge de estado de Webhook */}
                {integration?.webhook_active && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-600 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <div>
                        <h4 className="font-bold text-green-900">Webhook Activo</h4>
                        <p className="text-sm text-green-800">
                          Las órdenes y cambios de stock se sincronizan automáticamente en tiempo real desde PrestaShop.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                

                {/* VISTA RESYNC UNIFICADA (integración existente) */}
                {currentStage === 'resync' && (
                  <ResyncView
                    status={syncStatus}
                    loading={loadingStatus}
                    syncing={syncing || batchSyncing}
                    stockSyncing={syncingStock}
                    resourceProgress={resourceProgress}
                    batchProgress={batchSyncing ? batchProgress : null}
                    stockProgress={stockProgress}
                    webhookReady={!!(syncStatus?.resources?.categories?.synced && syncStatus?.resources?.products?.synced)}
                    onConfigureWebhook={() => setShowWebhookConfig(true)}
                    onReloadStatus={() => loadSyncStatus(integrationId)}
                    onSyncCategories={() => handleSyncResources(['categories'])}
                    onSyncProducts={handleBatchSync}
                    onSyncStock={handleSyncStockFromPrestaShop}
                    onSyncExtras={(list) => handleSyncResources(list)}
                  />
                )}
                
                {/* PRIMERA CONFIGURACIÓN: Categorías + Productos en un solo paso */}
                {currentStage === 'stage1' && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-600 rounded-xl p-4">
                      <h3 className="font-bold text-lg text-slate-900 mb-2">
                        Configuración inicial
                      </h3>
                      <p className="text-sm text-slate-700">
                        Primero se sincronizan las <strong>categorías</strong> y a continuación
                        los <strong>productos con su stock</strong>. Cuando arranquen los productos
                        verás el avance y podrás sincronizar el resto de recursos.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="border-2 rounded-xl p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-600">
                        <div className="flex items-start gap-3">
                          <Tag className="w-6 h-6 flex-shrink-0" />
                          <div>
                            <h4 className="font-bold text-slate-900 mb-1">Paso 1 · Categorías</h4>
                            <p className="text-sm text-slate-700">Estructura de categorías y subcategorías de tu tienda</p>
                          </div>
                        </div>
                      </div>
                      <div className="border-2 rounded-xl p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-600">
                        <div className="flex items-start gap-3">
                          <Package className="w-6 h-6 flex-shrink-0" />
                          <div>
                            <h4 className="font-bold text-slate-900 mb-1">Paso 2 · Productos + Stock</h4>
                            <p className="text-sm text-slate-700">Marca, precio, stock, imagen, SKU, código de barra, peso</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between gap-3 pt-4 border-t-2 border-slate-200">
                      <button
                        onClick={onClose}
                        className="px-6 py-3 bg-slate-100 border-2 border-slate-900 rounded-xl font-bold hover:bg-slate-200 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleFirstRun}
                        disabled={syncing || batchSyncing}
                        className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white border-2 border-slate-900 rounded-xl font-bold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
                      >
                        {(syncing || batchSyncing) ? (
                          <><Loader className="w-5 h-5 animate-spin" /> Sincronizando...</>
                        ) : (
                          <><RefreshCw className="w-5 h-5" /> Sincronizar Categorías y Productos</>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* ETAPA 2: Productos (obsoleta, se maneja en stage1 + resync) */}
                {false && currentStage === 'stage2' && (
                  <div className="space-y-4">
                    {completedStages.stage2 && (
                      <div className="bg-blue-50 border-2 border-blue-600 rounded-xl p-3">
                        <p className="text-sm text-blue-900 font-semibold">
                          ℹ️ Esta etapa ya fue completada. Puedes resincronizar para actualizar los productos.
                        </p>
                      </div>
                    )}
                    
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-600 rounded-xl p-4">
                      <h3 className="font-bold text-lg text-slate-900 mb-2">
                        {SYNC_STAGES.stage2.title}
                      </h3>
                      <p className="text-sm text-slate-700">
                        {SYNC_STAGES.stage2.description}
                      </p>
                    </div>
                    
                    {/* Recursos de Etapa 2 */}
                    <div className="space-y-3">
                      {SYNC_STAGES.stage2.resources.map((resource) => {
                        const Icon = resource.icon;
                        const colorClasses = {
                          green: 'from-green-50 to-green-100 border-green-600',
                          purple: 'from-purple-50 to-purple-100 border-purple-600',
                          orange: 'from-orange-50 to-orange-100 border-orange-600',
                          teal: 'from-teal-50 to-teal-100 border-teal-600',
                          pink: 'from-pink-50 to-pink-100 border-pink-600',
                          red: 'from-red-50 to-red-100 border-red-600'
                        };

                        return (
                          <div
                            key={resource.id}
                            className={`border-2 rounded-xl p-4 bg-gradient-to-br ${colorClasses[resource.color]}`}
                          >
                            <div className="flex items-start gap-3">
                              <Icon className="w-6 h-6 flex-shrink-0" />
                              <div className="flex-1">
                                <h4 className="font-bold text-slate-900 mb-1">
                                  {resource.label}
                                </h4>
                                <p className="text-sm text-slate-700">
                                  {resource.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex justify-between gap-3 pt-4 border-t-2 border-slate-200">
                      <button
                        onClick={onClose}
                        className="px-6 py-3 bg-slate-100 border-2 border-slate-900 rounded-xl font-bold hover:bg-slate-200 transition-colors"
                      >
                        Cancelar
                      </button>
                      
                      <button
                        onClick={handleBatchSync}
                        disabled={batchSyncing}
                        className="flex items-center gap-2 px-6 py-3 bg-purple-500 text-white border-2 border-slate-900 rounded-xl font-bold hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
                      >
                        {batchSyncing ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Sincronizando...
                          </>
                        ) : (
                          <>
                            <Package className="w-5 h-5" />
                            Sincronizar Productos
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* ETAPA 3: obsoleta, reemplazada por la vista resync unificada */}
                {false && currentStage === 'stage3' && (
                  <div className="space-y-4">
                    {/* Banner informativo */}
                    {(completedStages.stage1 || completedStages.stage2) ? (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-600 rounded-xl p-4">
                        <h3 className="font-bold text-lg text-slate-900 mb-2">
                          ✓ Resincronizar Recursos
                        </h3>
                        <p className="text-sm text-slate-700">
                          Ya tienes una integración activa. Selecciona los recursos que deseas actualizar.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-600 rounded-xl p-4">
                        <h3 className="font-bold text-lg text-slate-900 mb-2">
                          {SYNC_STAGES.stage3.title}
                        </h3>
                        <p className="text-sm text-slate-700">
                          {SYNC_STAGES.stage3.description}
                        </p>
                      </div>
                    )}
                    
                    {/* SI HAY INTEGRACIÓN PREVIA: Mostrar Categorías y Productos también */}
                    {(completedStages.stage1 || completedStages.stage2) && (
                      <div className="space-y-3">
                        <p className="text-sm font-bold text-slate-600 uppercase tracking-wide">
                          Recursos Base
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Categorías */}
                          <div
                            className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                              syncResources.categories 
                                ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-600' 
                                : 'bg-slate-50 border-slate-300 hover:border-slate-400'
                            }`}
                            onClick={() => setSyncResources(prev => ({ ...prev, categories: !prev.categories }))}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={syncResources.categories || false}
                                onChange={() => {}}
                                className="w-5 h-5 rounded border-2 border-slate-900 cursor-pointer mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Tag className="w-5 h-5" />
                                  <h4 className="font-bold text-slate-900">
                                    Categorías {completedStages.stage1 && <span className="text-green-600">✓</span>}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-700">
                                  Actualizar categorías de productos
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Productos */}
                          <div
                            className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                              syncResources.products 
                                ? 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-600' 
                                : 'bg-slate-50 border-slate-300 hover:border-slate-400'
                            }`}
                            onClick={() => setSyncResources(prev => ({ ...prev, products: !prev.products }))}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={syncResources.products || false}
                                onChange={() => {}}
                                className="w-5 h-5 rounded border-2 border-slate-900 cursor-pointer mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Package className="w-5 h-5" />
                                  <h4 className="font-bold text-slate-900">
                                    Productos {completedStages.stage2 && <span className="text-purple-600">✓</span>}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-700">
                                  Actualizar catálogo completo de productos
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-sm font-bold text-slate-600 uppercase tracking-wide mt-4">
                          Recursos Adicionales
                        </p>
                      </div>
                    )}
                    
                    {/* Recursos de Etapa 3 (órdenes, clientes, etc) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {SYNC_STAGES.stage3.resources.map((resource) => {
                        const Icon = resource.icon;
                        const isSelected = syncResources[resource.id] || false;
                        const colorClasses = {
                          green: 'from-green-50 to-green-100 border-green-600',
                          purple: 'from-purple-50 to-purple-100 border-purple-600',
                          orange: 'from-orange-50 to-orange-100 border-orange-600',
                          teal: 'from-teal-50 to-teal-100 border-teal-600',
                          pink: 'from-pink-50 to-pink-100 border-pink-600',
                          red: 'from-red-50 to-red-100 border-red-600'
                        };

                        return (
                          <div
                            key={resource.id}
                            className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                              isSelected 
                                ? `bg-gradient-to-br ${colorClasses[resource.color]}` 
                                : 'bg-slate-50 border-slate-300 hover:border-slate-400'
                            }`}
                            onClick={() => setSyncResources(prev => ({ ...prev, [resource.id]: !prev[resource.id] }))}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-5 h-5 rounded border-2 border-slate-900 cursor-pointer mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Icon className="w-5 h-5" />
                                  <h4 className="font-bold text-slate-900">
                                    {resource.label}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-700">
                                  {resource.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex justify-between gap-3 pt-4 border-t-2 border-slate-200">
                      <button
                        onClick={onClose}
                        className="px-6 py-3 bg-slate-100 border-2 border-slate-900 rounded-xl font-bold hover:bg-slate-200 transition-colors"
                      >
                        Cerrar
                      </button>
                      
                      <button
                        onClick={() => {
                          const selectedResources = Object.keys(syncResources).filter(k => syncResources[k]);
                          if (selectedResources.length === 0) {
                            toast.error('Selecciona al menos un recurso');
                            return;
                          }
                          
                          // Si seleccionó productos, usar batch sync
                          if (selectedResources.includes('products')) {
                            handleBatchSync();
                          } else {
                            // Para otros recursos (categorías, órdenes, etc), usar sync normal
                            handleSyncResources(selectedResources);
                          }
                        }}
                        disabled={syncing || batchSyncing}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white border-2 border-slate-900 rounded-xl font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
                      >
                        {(syncing || batchSyncing) ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Sincronizando...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-5 h-5" />
                            Sincronizar Recursos
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Sección de Stock eliminada: el stock se sincroniza junto con los productos */}
                {false && (
                  <div className="mt-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-600 rounded-xl">
                    <h3 className="font-bold text-lg text-slate-900 mb-2 flex items-center gap-2">
                      <Package className="w-6 h-6 text-blue-600" />
                      Sincronización de Stock
                    </h3>
                    <p className="text-sm text-slate-700 mb-4">
                      Mantén el stock sincronizado entre Negocio Feliz y PrestaShop
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Botón: Sincronizar Stock desde PrestaShop */}
                      <button
                        onClick={handleSyncStockFromPrestaShop}
                        disabled={syncingStock}
                        className="flex items-center gap-2 px-4 py-3 bg-blue-500 text-white border-2 border-slate-900 rounded-lg font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        style={{ boxShadow: '3px 3px 0px 0px rgba(15,23,42,1)' }}
                      >
                        {syncingStock ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Sincronizando...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-5 h-5" />
                            Sincronizar Stock desde PS
                          </>
                        )}
                      </button>
                      
                      {/* Botón: Detectar Conflictos */}
                      <button
                        onClick={handleDetectConflicts}
                        disabled={loadingConflicts}
                        className="flex items-center gap-2 px-4 py-3 bg-yellow-500 text-white border-2 border-slate-900 rounded-lg font-bold hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        style={{ boxShadow: '3px 3px 0px 0px rgba(15,23,42,1)' }}
                      >
                        {loadingConflicts ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Buscando...
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-5 h-5" />
                            Detectar Conflictos
                          </>
                        )}
                      </button>
                    </div>
                    
                    <div className="mt-3 p-3 bg-blue-100 border-2 border-blue-600 rounded-lg">
                      <p className="text-xs text-slate-700">
                        <span className="font-bold">💡 Nota:</span> La sincronización de stock también se ejecuta automáticamente al editar stock en Inventario o al realizar ventas en POS.
                      </p>
                    </div>
                  </div>
                )}

                {/* Barra de progreso genérica (solo fuera de la vista resync, que
                    ya muestra el avance por recurso en cada tarjeta) */}
                {syncing && currentStage !== 'resync' && (
                  <div className="bg-white border-2 border-slate-900 rounded-xl p-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-700 font-semibold">Sincronizando recursos...</span>
                      <span className="text-slate-600 font-bold">{Math.round(syncProgress)}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3 border-2 border-slate-900 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-pink-500 to-rose-600 h-full transition-all duration-500 ease-out"
                        style={{ width: `${syncProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                      <Loader className="w-3 h-3 animate-spin" />
                      Este proceso puede tardar varios minutos dependiendo de la cantidad de datos...
                    </p>
                  </div>
                )}
                
                {/* Barra de Progreso de Sincronización por Lotes (detalle; en la vista
                    resync el avance ya se ve en la tarjeta de Productos) */}
                {batchSyncing && currentStage !== 'resync' && (
                  <div className="mt-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-600 rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-lg text-slate-900">Sincronización en Progreso</h3>
                      <div className="text-2xl font-bold text-green-600">
                        {batchProgress.progress_percentage}%
                      </div>
                    </div>
                    
                    {/* Barra de progreso visual */}
                    <div className="w-full h-6 bg-slate-200 rounded-full overflow-hidden mb-4 border-2 border-slate-900">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500 ease-out flex items-center justify-end pr-2"
                        style={{ width: `${batchProgress.progress_percentage}%` }}
                      >
                        {batchProgress.progress_percentage > 10 && (
                          <span className="text-white font-bold text-sm">
                            {batchProgress.progress_percentage}%
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Estadísticas detalladas */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-white p-3 rounded-lg border-2 border-slate-900">
                        <div className="text-xs text-slate-600 mb-1">Lote Actual</div>
                        <div className="text-xl font-bold text-slate-900">
                          {batchProgress.total_batches > 0
                            ? `${Math.min(batchProgress.current_batch, batchProgress.total_batches)} / ${batchProgress.total_batches}`
                            : `${batchProgress.current_batch} / …`}
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border-2 border-slate-900">
                        <div className="text-xs text-slate-600 mb-1">Total Productos</div>
                        <div className="text-xl font-bold text-slate-900">
                          {batchProgress.total_products}
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border-2 border-slate-900">
                        <div className="text-xs text-green-600 mb-1">✓ Sincronizados</div>
                        <div className="text-xl font-bold text-green-600">
                          {batchProgress.synced_products}
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border-2 border-slate-900">
                        <div className="text-xs text-red-600 mb-1">✗ Fallidos</div>
                        <div className="text-xl font-bold text-red-600">
                          {batchProgress.failed_products}
                        </div>
                      </div>
                    </div>
                    
                    {/* Mensaje de estado */}
                    <div className="text-center text-sm text-slate-700 font-medium">
                      {batchProgress.total_batches > 0 && (
                        <p>
                          Procesando lote {batchProgress.current_batch} de {batchProgress.total_batches}: 
                          <span className="font-bold text-green-600 ml-2">
                            {batchProgress.synced_products}/{batchProgress.total_products} productos
                          </span>
                        </p>
                      )}
                      {batchProgress.status === 'starting' && (
                        <p className="text-blue-600 font-bold">Iniciando sincronización...</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Reporte Final */}
                {showBatchReport && !batchSyncing && (
                  <div className="mt-6 p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-600 rounded-xl">
                    <h3 className="font-bold text-lg text-slate-900 mb-4">📊 Reporte de Sincronización</h3>
                    
                    <div className="space-y-3">
                      {batchProgress.errors_count > 0 && (
                        <div className="p-4 bg-red-50 border-2 border-red-600 rounded-lg">
                          <p className="text-red-900 font-bold">
                            ⚠️ {batchProgress.errors_count} productos fallaron
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            Revisa el reporte para ver detalles
                          </p>
                        </div>
                      )}
                      
                      {batchProgress.incomplete_count > 0 && (
                        <div className="p-4 bg-orange-50 border-2 border-orange-600 rounded-lg">
                          <p className="text-orange-900 font-bold">
                            📝 {batchProgress.incomplete_count} productos con datos incompletos
                          </p>
                          <p className="text-sm text-orange-700 mt-1">
                            (Sin imagen, descripción, marca o categoría)
                          </p>
                        </div>
                      )}
                      
                      <button
                        onClick={downloadBatchReport}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white border-2 border-slate-900 rounded-lg font-bold hover:bg-blue-600 transition-all"
                        style={{ boxShadow: '3px 3px 0px 0px rgba(15,23,42,1)' }}
                      >
                        <FileText className="w-5 h-5" />
                        Descargar Reporte Completo (JSON)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de configuración de Webhook */}
      {showWebhookConfig && (
        <WebhookConfigModal
          integrationId={integrationId}
          onClose={() => setShowWebhookConfig(false)}
        />
      )}

      {/* Modal de Conflictos de Stock */}
      {showConflictsModal && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setShowConflictsModal(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
            <div 
              className="bg-white border-4 border-slate-900 rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden"
              style={{ boxShadow: '12px 12px 0px 0px rgba(15,23,42,1)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-6 py-4 flex justify-between items-center border-b-4 border-slate-900">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <ShoppingCart className="w-6 h-6" />
                  Conflictos de Stock ({stockConflicts.length})
                </h2>
                <button
                  onClick={() => setShowConflictsModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Contenido */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                {stockConflicts.length === 0 ? (
                  <div className="text-center py-8">
                    <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      ¡Sin conflictos!
                    </h3>
                    <p className="text-slate-600">
                      El stock está sincronizado correctamente entre Negocio Feliz y PrestaShop.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {stockConflicts.map((conflict, index) => (
                      <div 
                        key={index}
                        className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-600 rounded-xl p-4"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-bold text-slate-900">{conflict.product_name}</h4>
                            <p className="text-sm text-slate-600">SKU: {conflict.sku}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs font-bold text-red-600">
                              Diferencia: {conflict.difference}
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="bg-blue-100 border-2 border-blue-600 rounded-lg p-3">
                            <div className="text-xs text-slate-600 mb-1">Negocio Feliz</div>
                            <div className="text-2xl font-bold text-blue-600">{conflict.local_stock}</div>
                          </div>
                          <div className="bg-purple-100 border-2 border-purple-600 rounded-lg p-3">
                            <div className="text-xs text-slate-600 mb-1">PrestaShop</div>
                            <div className="text-2xl font-bold text-purple-600">{conflict.prestashop_stock}</div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleResolveConflict(conflict.product_id, 'use_local')}
                            className="flex-1 px-3 py-2 bg-blue-500 text-white border-2 border-slate-900 rounded-lg text-sm font-bold hover:bg-blue-600 transition-all"
                            style={{ boxShadow: '2px 2px 0px 0px rgba(15,23,42,1)' }}
                          >
                            Usar Local ({conflict.local_stock})
                          </button>
                          <button
                            onClick={() => handleResolveConflict(conflict.product_id, 'use_prestashop')}
                            className="flex-1 px-3 py-2 bg-purple-500 text-white border-2 border-slate-900 rounded-lg text-sm font-bold hover:bg-purple-600 transition-all"
                            style={{ boxShadow: '2px 2px 0px 0px rgba(15,23,42,1)' }}
                          >
                            Usar PrestaShop ({conflict.prestashop_stock})
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PrestashopModal;
